import { PageData, PageDataParams } from './page-data';
import {
  Scenario,
  PageType,
  RawInputPageData,
  Hints,
  TagResult,
} from '../common/page-data';
import { ProblemOption } from '../common/problem-option';

export interface ConstructScore {
  answeredCorrectly: number;
  answeredIncorrectly: number;
  correctAnswers: number;
  enoughAnsweredCorrectly: boolean;
  score: number;
}
export class ConstructData extends PageData {
  private _options: ProblemOption[];

  public get options(): ProblemOption[] {
    return this._options;
  }
  public get scenario(): Scenario | undefined {
    return this._scenario;
  }

  protected _hints: Hints;
  public get hints(): Hints {
    return this._hints;
  }

  public get score(): number {
    return this.constructScore.score;
  }

  public get constructScore(): ConstructScore {
    const CORRECT_THRESHOLD = 0.95;

    const score: ConstructScore = {
      answeredCorrectly: 0,
      answeredIncorrectly: 0,
      correctAnswers: 0,
      enoughAnsweredCorrectly: false,
      score: 0,
    };

    this._options.forEach((option): void => {
      if (option.isCorrectResponse) {
        score.correctAnswers++;
        score.answeredCorrectly += option.isSelected ? 1 : 0;
        score.answeredIncorrectly += option.isSelected ? 0 : 1;
      } else {
        score.answeredIncorrectly += option.isSelected ? 1 : 0;
      }
    });

    const { correctAnswers, answeredCorrectly, answeredIncorrectly } = score;
    score.enoughAnsweredCorrectly =
      answeredCorrectly / correctAnswers >= CORRECT_THRESHOLD;

    if (
      answeredIncorrectly ||
      correctAnswers <= 3 ||
      answeredCorrectly / correctAnswers / CORRECT_THRESHOLD < 1
    ) {
      score.score =
        (answeredCorrectly - Math.min(answeredCorrectly, answeredIncorrectly)) /
        correctAnswers;
    } else {
      score.score = 1;
    }

    return score;
  }

  public get pageType(): PageType {
    return PageType.Problem;
  }

  public get rawData(): RawInputPageData {
    const options = this._options.map((v) => v.getRawData());
    const playStats = this.playStats;

    const raw: RawInputPageData = {
      ...(this._rawPageData as RawInputPageData),
      options,
      playStats,
    };

    return raw;
  }

  public get tagResults(): TagResult[] {
    const tagResults: TagResult[] = [];

    this.options.forEach((option) => {
      const correct = option.isCorrectResponse === option.isSelected;
      const tags = option.tags;

      if (tags) {
        tags.forEach((tag) => {
          tagResults.push({ tagId: tag.id, name: tag.name, correct });
        });
      }
    });

    return tagResults;
  }

  public reset(): void {
    this._options.forEach((option) => {
      option.isSelected = false;
    });
  }

  public constructor(params: PageDataParams) {
    super(params);

    const { rawPageData } = params;

    const rawInputPageData = rawPageData as RawInputPageData;
    this._hints = rawInputPageData.hints;
    this._options = rawInputPageData.options.map((v): ProblemOption => {
      return new ProblemOption(
        v,
        this.problemQuality,
        this._rawPageData.type,
        this.skill
      );
    });
  }
}
